// PATH: src/hooks/useThemeMode.ts — thème clair / sombre / système.
//
// Trois états et non deux : « système » suit le réglage du poste et change avec
// lui (un agent qui bascule son OS en sombre le soir n'a rien à faire ici),
// tandis qu'un choix explicite doit être respecté même s'il contredit l'OS.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "tilmischool.theme";

export type ThemeMode = "light" | "dark" | "system";

const read = (): ThemeMode => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
  } catch {
    return "system";
  }
};

const systemPrefersDark = (): boolean =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;

export interface ThemeModeState {
  mode: ThemeMode;
  /** Thème effectivement appliqué, « système » déjà résolu. */
  dark: boolean;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeMode = (): ThemeModeState => {
  const [mode, setModeState] = useState<ThemeMode>(read);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Suit le réglage du poste même s'il change en cours de session.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const dark = mode === "system" ? systemDark : mode === "dark";

  // `data-theme` sur <html> : c'est par lui que la feuille de style ajuste ce
  // qu'antd ne gère pas (barres de défilement, bordures de cartes).
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  }, [dark]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* préférence non mémorisée, sans conséquence sur la session */
    }
  }, []);

  return { mode, dark, setMode };
};
